import React, { useCallback, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { PointOfSale as CashIcon } from '@mui/icons-material';
import OpenCashRegisterDialog from '../components/financial/OpenCashRegisterDialog';
import {
  getCashRegisterErrorMessage,
  isCashRegisterRequiredError,
} from '../utils/cashRegisterPrompt';
import { usePageBreakpoints } from './useClinicPageBreakpoints';

type PromptStep = 'confirm' | 'form';

interface PromptState {
  step: PromptStep;
  message: string;
  onOpened?: () => void | Promise<void>;
}

/**
 * Trata o erro 412 `CASH_REGISTER_REQUIRED` com confirmação e abertura
 * do caixa na própria tela — sem redirecionar para /financial/cash-register.
 */
export function useCashRegisterPrompt() {
  const { isMobile } = usePageBreakpoints();
  const [state, setState] = useState<PromptState | null>(null);

  const closePrompt = useCallback(() => setState(null), []);

  const handleCashRegisterRequired = useCallback(
    (err: unknown, onOpened?: () => void | Promise<void>): boolean => {
      if (!isCashRegisterRequiredError(err)) return false;
      setState({
        step: 'confirm',
        message: getCashRegisterErrorMessage(err),
        onOpened,
      });
      return true;
    },
    [],
  );

  const cashRegisterPromptUi = (
    <>
      <Dialog
        open={state?.step === 'confirm'}
        onClose={closePrompt}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CashIcon sx={{ color: '#0f766e' }} />
          Caixa fechado
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {state?.message || 'Abra o caixa do dia antes de prosseguir.'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1.5 }}>
            Deseja abrir o caixa agora? Você continua nesta tela.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closePrompt}>Agora não</Button>
          <Button
            variant="contained"
            onClick={() =>
              setState((prev) =>
                prev ? { ...prev, step: 'form' } : prev,
              )
            }
          >
            Abrir caixa
          </Button>
        </DialogActions>
      </Dialog>

      <OpenCashRegisterDialog
        open={state?.step === 'form'}
        onClose={closePrompt}
        onOpened={state?.onOpened}
      />
    </>
  );

  return { handleCashRegisterRequired, cashRegisterPromptUi, closeCashRegisterPrompt: closePrompt };
}

export default useCashRegisterPrompt;
